import { Col, Content, FlexboxGrid, Grid, Row, Button, Text } from 'rsuite';
import PortalHeader from '../../../components/layout/PortalHeader';
import usePlaceNavigate from '../../../hooks/usePlaceNavigate';
import PersonDetails from '../../../components/form/PersonDetails';
import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectWorkErrorMessage,
  selectWorkFailed,
  selectWorkStarted,
  selectWorkSuccess,
  selectCurrentUser,
} from '../../../store/slices/auth/authSelectors';
import pluralize from 'pluralize';
import IconIndicator from '../../../components/user-feedback/IconIndicator';
import { selectConfigExpressRegistrationTypeVerbiage } from '../../../store/slices/config/configSelectors';
import { updateAccountDetails } from '../../../store/slices/auth/authThunks';
import Notice from '../../../components/user-feedback/Notice';
import {
  companyNameValidation,
  phoneRequiredValidation,
  requiredValidation,
} from '../../../common/validation';
import { isNullUndefinedOrEmptyObject } from '../../../common/utils';
import { ConfirmationModal } from './ConfirmationModal';
import { initialPerson } from 'src/common/people';

const UpdateAccount = () => {
  const placeNavigate = usePlaceNavigate();
  const form = useRef();
  const dispatch = useDispatch();

  const [formError, setFormError] = useState({});
  const [formValues, setFormValues] = useState({});
  const [addressComponents, setAddressComponents] = useState({});
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [successMessageVisible, setSuccessMessageVisible] = useState(false);
  const [errorMessageVisible, setErrorMessageVisible] = useState(false);

  const userDetails = useSelector(selectCurrentUser);
  const permitType = useSelector(selectConfigExpressRegistrationTypeVerbiage);

  const updatingDetails = useSelector(selectWorkStarted);
  const updatingDetailsSuccess = useSelector(selectWorkSuccess);
  const updatingDetailsFail = useSelector(selectWorkFailed);
  const updatingDetailsErrorMessage = useSelector(selectWorkErrorMessage);

  const regVerbiage = useSelector(selectConfigExpressRegistrationTypeVerbiage);

  // Redirect to login if no userDetails
  useEffect(() => {
    if (!userDetails) {
      placeNavigate('/');
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails]);

  const formModel = useMemo(() => {
    return {
      ...initialPerson,
    };
  }, []);

  useEffect(() => {
    const addressComponents = userDetails?.misc?.mailingAddressComponents ?? {};
    setAddressComponents(addressComponents);
  }, [userDetails]);

  useEffect(() => {
    if (!isNullUndefinedOrEmptyObject(userDetails)) {
      setFormValues({
        email: userDetails?.email ?? '',
        firstName: userDetails?.firstName ?? '',
        lastName: userDetails?.lastName ?? '',
        phone: userDetails?.phone ?? '',
        companyName: userDetails?.companyName ?? '',
        mailingAddress: userDetails?.mailingAddress ?? '',
      });
    }
  }, [userDetails]);

  // Validation
  const validationModel = {
    // don't need validation for disabled inputs
    phone: phoneRequiredValidation,
    companyName: companyNameValidation,
    mailingAddress: requiredValidation,
  };

  const validateForm = () => {
    return form.current.check();
  };

  const openConfirmationModal = () => {
    const isValid = validateForm();

    if (isValid) {
      setConfirmationModalOpen(true);
    }
  };

  const updatePersonalDetails = async () => {
    setSuccessMessageVisible(false);
    setErrorMessageVisible(false);

    const isValid = validateForm();

    if (isValid) {
      const updatedData = {
        ...formValues,
        mailingAddressComponents: addressComponents,
      };
      dispatch(updateAccountDetails(userDetails?.userId, updatedData));
    }
  };

  useEffect(() => {
    setConfirmationModalOpen(false);
    if (updatingDetailsSuccess) {
      setSuccessMessageVisible(true);
      const timer = setTimeout(() => {
        setSuccessMessageVisible(false);
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    } else {
      setSuccessMessageVisible(false);
    }
  }, [updatingDetailsSuccess]);

  useEffect(() => {
    setConfirmationModalOpen(false);
    setErrorMessageVisible(!!updatingDetailsFail);
  }, [updatingDetailsFail]);

  return (
    <>
      <FlexboxGrid justify="space-between">
        <FlexboxGrid.Item colspan={24}>
          <PortalHeader title={`${regVerbiage} Registration Application`} />
        </FlexboxGrid.Item>
        <FlexboxGrid.Item
          as="main"
          id="main-content"
          tabIndex={-1}
          colspan={24}
          className="sm-page-padding"
          aria-label={`${regVerbiage} Registration Application`}
        >
          <Content as="div">
            <Grid className="mt-24">
              <Row className="mb-24">
                <Col xs={24}>
                  <FlexboxGrid>
                    <FlexboxGrid.Item style={{ paddingRight: '20px' }}>
                      <Button
                        appearance="subtle"
                        className="link-gray"
                        onClick={() => {
                          placeNavigate(-1);
                        }}
                      >
                        &lt; Back
                      </Button>
                    </FlexboxGrid.Item>
                  </FlexboxGrid>
                </Col>
              </Row>
              <Row>
                <Col xs={24}>
                  <FlexboxGrid>
                    <FlexboxGrid.Item>
                      <Text className="header-text-h3 mb-24">
                        Update Details
                      </Text>
                    </FlexboxGrid.Item>
                  </FlexboxGrid>
                </Col>
              </Row>

              <Row className="content-section">
                <Col xs={24}>
                  <div role="grid">
                    <Row className="mb-24">
                      <Col xs={24}>
                        <Text>
                          Please update your details below. Note that you cannot
                          change some details.
                        </Text>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={24}>
                        <PersonDetails
                          formModel={formModel}
                          ref={form}
                          setFormError={setFormError}
                          setFormValues={setFormValues}
                          formValues={formValues}
                          formError={formError}
                          hiddenFields={{
                            physicalAddress: true,
                            sameAsPropertyAddress: true,
                          }}
                          optionalFields={{ companyName: 'optional' }}
                          validationModel={validationModel}
                          disabledFields={{
                            email: true,
                            firstName: true,
                            lastName: true,
                          }}
                        />
                      </Col>
                    </Row>

                    {updatingDetails && (
                      <Row>
                        <Col xs={24}>
                          <IconIndicator />
                        </Col>
                      </Row>
                    )}

                    {successMessageVisible && !updatingDetails && (
                      <Row className="mb-12">
                        <Col xs={24}>
                          <Notice
                            type="success"
                            message="Your details have been updated successfully."
                          />
                        </Col>
                      </Row>
                    )}

                    {errorMessageVisible && !updatingDetails && (
                      <Row className="mb-12">
                        <Col xs={24}>
                          <Notice message={updatingDetailsErrorMessage} />
                        </Col>
                      </Row>
                    )}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col
                  xs={24}
                  className="mt-16"
                >
                  <Button
                    className="btn-primary sm-100-percent"
                    data-cy="update-details-form-button"
                    onClick={() => {
                      openConfirmationModal();
                    }}
                  >
                    Update Details
                  </Button>
                </Col>
              </Row>
            </Grid>
          </Content>
        </FlexboxGrid.Item>
      </FlexboxGrid>
      <ConfirmationModal
        open={confirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
        permitType={pluralize(permitType).toLocaleLowerCase()}
        loading={updatingDetails}
        onAccept={updatePersonalDetails}
      />
    </>
  );
};

export default UpdateAccount;
